import { useMemo, useState, type FormEvent } from "react";
import useSeo, { SITE_ORIGIN } from "@/hooks/useSeo";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import FaqSection from "@/pages/home/components/FaqSection";
import { siteInfo } from "@/mocks/categories";
import { faqs } from "@/mocks/faqs";
import { submitFormData } from "@/utils/submitForm";

const CONTACT_URL = "https://readdy.ai/api/form/dahfoqbp14a1h3mr6l30";

const topicOptions = ["公开线索提供", "内容纠错与补充", "版权与转载", "合作与投稿", "其他事项"];

const contactCards = [
  {
    icon: "ri-mail-send-line",
    label: "日常联系",
    value: siteInfo.email,
    note: "一般会在两个工作日内回复。",
  },
  {
    icon: "ri-lightbulb-line",
    label: "线索投递",
    value: siteInfo.tipEmail,
    note: "请附上公开来源链接，便于我们核验。",
  },
  {
    icon: "ri-time-line",
    label: "内容更新",
    value: "工作日 09:00 – 23:00",
    note: "热榜每小时刷新，分类页每天集中更新。",
  },
];

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");
  const [textLength, setTextLength] = useState(0);

  const jsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "ContactPage",
      name: "联系吃瓜前线",
      url: `${SITE_ORIGIN}/contact`,
      inLanguage: "zh-CN",
      breadcrumb: {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: SITE_ORIGIN },
          { "@type": "ListItem", position: 2, name: "联系我们", item: `${SITE_ORIGIN}/contact` },
        ],
      },
      mainEntity: {
        "@type": "Organization",
        name: siteInfo.name,
        email: siteInfo.email,
      },
    }),
    []
  );

  useSeo({
    title: "联系我们 | 提供网爆吃瓜线索与内容纠错",
    description:
      "联系吃瓜前线，提供公开可见的网爆吃瓜线索、纠正内容或沟通转载合作。本页说明联系渠道与线索提交要求，并附常见问题。",
    keywords: "联系吃瓜前线,网爆吃瓜线索,网爆吃瓜第一手爆料,内容纠错",
    path: "/contact",
    jsonLd,
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === "loading") return;
    const formEl = event.currentTarget;
    setStatus("loading");
    setMessage("");

    const result = await submitFormData(
      CONTACT_URL,
      formEl,
      "已收到你的信息，我们会尽快查看并回复。"
    );

    if (result.ok) {
      setStatus("success");
      setMessage(result.message);
      formEl.reset();
      setTextLength(0);
    } else {
      setStatus("error");
      setMessage(result.message);
    }
  };

  return (
    <div className="w-full">
      <div className="border-b border-background-200 bg-background-100 px-4 pb-10 pt-8 md:px-8 md:pb-12 md:pt-10">
        <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "联系我们" }]} />
        <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 font-label text-xs font-medium text-accent-800">
          <i className="ri-chat-smile-2-line" aria-hidden="true"></i>
          联系我们
        </span>
        <h1 className="mt-4 max-w-3xl font-heading text-2xl font-black leading-tight tracking-tight text-foreground-950 md:text-4xl">
          有线索、有补充，或者只是想聊聊
        </h1>
        <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
          无论是公开线索、内容纠错，还是转载与合作沟通，都可以通过下面的表单或邮箱联系我们。请留意：涉及他人隐私与未经证实指控的内容，我们不会收录。
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 px-4 py-10 md:px-8 md:py-12 lg:grid-cols-3">
        {contactCards.map((card) => (
          <div key={card.label} className="rounded-lg border border-background-200 bg-background-50 p-6">
            <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-700">
              <i className={`${card.icon} text-xl`} aria-hidden="true"></i>
            </span>
            <h2 className="mt-4 font-label text-sm text-foreground-500">{card.label}</h2>
            <p className="mt-1 font-heading text-base font-bold text-foreground-950">{card.value}</p>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">{card.note}</p>
          </div>
        ))}
      </div>

      <div className="px-4 pb-12 md:px-8 md:pb-16">
        <div className="grid grid-cols-1 gap-8 rounded-lg border border-background-200 bg-background-100 p-6 md:p-10 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">留下你的信息</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              带 * 的项目为必填。为了让沟通更高效，请在留言里尽量写清话题名称与你看到的公开来源。
            </p>
            <ul className="mt-5 flex flex-col gap-3 text-sm leading-relaxed text-foreground-600">
              <li className="flex gap-2">
                <i className="ri-checkbox-circle-line mt-0.5 text-primary-600" aria-hidden="true"></i>
                请勿提交个人隐私、身份信息或未经证实的内容。
              </li>
              <li className="flex gap-2">
                <i className="ri-checkbox-circle-line mt-0.5 text-primary-600" aria-hidden="true"></i>
                版权相关请附上作品名称与原始发布链接。
              </li>
              <li className="flex gap-2">
                <i className="ri-checkbox-circle-line mt-0.5 text-primary-600" aria-hidden="true"></i>
                表单仅用于联系与线索沟通，不会公开你的信息。
              </li>
            </ul>
          </div>

          <form
            id="contact-hotspot-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6 lg:col-span-3"
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-2">
                <label htmlFor="contact-name" className="font-label text-sm font-medium text-foreground-800">
                  称呼 *
                </label>
                <input
                  id="contact-name"
                  type="text"
                  name="name"
                  required
                  placeholder="怎么称呼你"
                  className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 font-label text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-1 focus:ring-primary-300 placeholder:text-foreground-400"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label htmlFor="contact-email" className="font-label text-sm font-medium text-foreground-800">
                  邮箱 *
                </label>
                <input
                  id="contact-email"
                  type="email"
                  name="email"
                  required
                  autoComplete="email"
                  placeholder="用于接收回复"
                  className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 font-label text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-1 focus:ring-primary-300 placeholder:text-foreground-400"
                />
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="contact-topic" className="font-label text-sm font-medium text-foreground-800">
                事项类型 *
              </label>
              <select
                id="contact-topic"
                name="topic"
                required
                defaultValue=""
                className="cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-2.5 font-label text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-1 focus:ring-primary-300"
              >
                <option value="" disabled>
                  请选择事项类型
                </option>
                {topicOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="contact-message" className="font-label text-sm font-medium text-foreground-800">
                留言内容 *
              </label>
              <textarea
                id="contact-message"
                name="message"
                required
                maxLength={500}
                rows={5}
                onChange={(event) => setTextLength(event.target.value.length)}
                placeholder="写清话题名称、你看到的公开来源，以及想补充的信息（500 字以内）"
                className="resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 font-label text-sm leading-relaxed text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-1 focus:ring-primary-300 placeholder:text-foreground-400"
              />
              <span className="self-end font-label text-xs text-foreground-500">{textLength} / 500</span>
            </div>

            <div className="contact-alt-wrap" aria-hidden="true">
              <label htmlFor="contact-website-alt">请勿填写此栏</label>
              <input
                id="contact-website-alt"
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden={true}
                readOnly
              />
            </div>

            <button
              type="submit"
              disabled={status === "loading"}
              className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {status === "loading" ? "提交中…" : "提交信息"}
            </button>

            {message && (
              <p
                role="status"
                className={`font-label text-sm ${status === "success" ? "text-primary-700" : "text-secondary-700"}`}
              >
                {status === "success" ? (
                  <i className="ri-checkbox-circle-line mr-1" aria-hidden="true"></i>
                ) : (
                  <i className="ri-error-warning-line mr-1" aria-hidden="true"></i>
                )}
                {message}
              </p>
            )}
          </form>
        </div>
      </div>

      <FaqSection items={faqs.slice(2, 6)} />
    </div>
  );
}